import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Mail, RefreshCw, Search, Inbox, Send, Star, AlertCircle, 
    FileText, Trash2, Loader2, ChevronRight,
    Paperclip, CheckSquare, Square, Edit3, Reply, Forward, X,
    Menu, PanelLeftOpen, PanelLeftClose, ShieldAlert
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import ComposeEmailModalV2 from '../components/ComposeEmailModalV2';
import ZimbraAccountActions from '../components/ZimbraAccountActions';
import { mailAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { useMailWebSocket } from '../hooks/useMailWebSocket';

const FOLDERS = [
    { id: 'inbox', label: 'Kotak Masuk', icon: Inbox, color: 'text-blue-600' },
    { id: 'sent', label: 'Terkirim', icon: Send, color: 'text-green-600' },
    { id: 'starred', label: 'Berbintang', icon: Star, color: 'text-yellow-500' },
    { id: 'important', label: 'Penting', icon: AlertCircle, color: 'text-red-600' },
    { id: 'spam', label: 'Spam', icon: ShieldAlert, color: 'text-orange-600' },
    { id: 'drafts', label: 'Draf', icon: FileText, color: 'text-gray-600' },
    { id: 'trash', label: 'Sampah', icon: Trash2, color: 'text-red-500' },
];

const PAGE_SIZE = 50;

function sanitizeHtml(html) {
    if (!html) return '';
    try {
        return DOMPurify.sanitize(html, {
            USE_PROFILES: { html: true },
            FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
            FORBID_ATTR: ['onerror', 'onload', 'onclick'],
        });
    } catch (e) {
        return html;
    }
}

function formatFullDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleString('id-ID', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

function formatShortDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    const now = new Date();
    const diffHours = Math.floor((now - date) / 3600000);
    if (diffHours < 24) {
        return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    } else if (diffHours < 168) {
        return date.toLocaleDateString('id-ID', { weekday: 'short' });
    } else {
        return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
    }
}

function truncateText(text, maxLength = 80) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}

export default function GmailLikeMailPage() {
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [emails, setEmails] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [selectedFolder, setSelectedFolder] = useState('inbox');
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedEmailIds, setSelectedEmailIds] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCompose, setShowCompose] = useState(false);
    const [composeDraft, setComposeDraft] = useState(null);
    const [accountInfo, setAccountInfo] = useState(null);
    const [hasSyncedOnce, setHasSyncedOnce] = useState(false);
    const [accessChecking, setAccessChecking] = useState(true);
    const [allowedAccess, setAllowedAccess] = useState(false);
    const navigate = useNavigate();
    // Auto-hide app sidebar while on Mail. User can toggle via button.
    const [appSidebarHidden, setAppSidebarHidden] = useState(() => {
        const saved = localStorage.getItem('mail_app_sidebar_hidden');
        return saved === null ? true : saved === 'true';
    });
    const listRef = useRef(null);
    // Default-open grace period: when user first lands on Mail, show the
    // sidebar briefly so they can orient themselves, then auto-collapse
    // with a smooth animation. Only the first render of this page triggers
    // the grace window; further toggles obey the persisted preference.
    const [initialGraceActive, setInitialGraceActive] = useState(true);
    // Transient hover state — when cursor is near the left edge OR over the
    // (briefly expanded) sidebar, we uncollapse without mutating the
    // persisted preference.
    const [sidebarHovered, setSidebarHovered] = useState(false);
    const sidebarHoverTimerRef = useRef(null);

    // Effective collapse state used by Layout. Collapses only when:
    //  - user's persisted preference says hidden AND
    //  - cursor isn't hovering the sidebar/edge trigger AND
    //  - initial grace window has elapsed.
    const effectiveSidebarCollapsed = appSidebarHidden && !sidebarHovered && !initialGraceActive;

    useEffect(() => {
        // Auto-close the default-open sidebar after 3s on mount.
        const t = setTimeout(() => setInitialGraceActive(false), 3000);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        return () => {
            if (sidebarHoverTimerRef.current) clearTimeout(sidebarHoverTimerRef.current);
        };
    }, []);

    const toggleAppSidebar = () => {
        // Any manual toggle ends the grace window immediately.
        setInitialGraceActive(false);
        setAppSidebarHidden(prev => {
            const next = !prev;
            localStorage.setItem('mail_app_sidebar_hidden', String(next));
            return next;
        });
    };

    const handleSidebarMouseEnter = () => {
        if (sidebarHoverTimerRef.current) {
            clearTimeout(sidebarHoverTimerRef.current);
            sidebarHoverTimerRef.current = null;
        }
        setSidebarHovered(true);
    };

    const handleSidebarMouseLeave = () => {
        if (sidebarHoverTimerRef.current) clearTimeout(sidebarHoverTimerRef.current);
        sidebarHoverTimerRef.current = setTimeout(() => {
            setSidebarHovered(false);
        }, 150);
    };

    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; }
    })();

    // Real-time push (WebSocket) — when ingress supports it
    useMailWebSocket(currentUser?.id, (event) => {
        if (event?.type === 'new_email') {
            const n = event.count || 0;
            if (n > 0) {
                toast.success(`${n} email baru masuk`, {
                    description: event.emails?.[0]?.subject || '',
                });
                if (event.unread_count !== undefined) setUnreadCount(event.unread_count);
                // Silent refresh if we're looking at inbox
                if (selectedFolder === 'inbox') {
                    loadEmails(0, false);
                }
            }
        }
    });

    // Polling fallback — every 60s silently refresh inbox unread so notifications
    // still reach the user even in preview environments where WS upgrade is blocked
    useEffect(() => {
        const t = setInterval(async () => {
            try {
                const res = await mailAPI.getEmails('inbox', { skip: 0, limit: 1 });
                const newUnread = res.data?.unread_count ?? 0;
                setUnreadCount(prev => {
                    if (newUnread > prev) {
                        const diff = newUnread - prev;
                        toast.success(`${diff} email baru masuk`);
                        if (selectedFolder === 'inbox') loadEmails(0, false);
                    }
                    return newUnread;
                });
            } catch (e) { /* noop */ }
        }, 60000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolder]);

    const loadAccountInfo = async () => {
        try {
            const response = await mailAPI.getAccount();
            if (response.data) setAccountInfo(response.data);
        } catch (err) {
            console.error('Failed to load account:', err);
        }
    };

    const loadEmails = useCallback(async (skip = 0, showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const response = await mailAPI.getEmails(selectedFolder, {
                search: searchQuery, skip, limit: PAGE_SIZE,
            });
            const data = response.data || {};
            const list = data.emails || [];
            setEmails(prev => skip === 0 ? list : [...prev, ...list]);
            setTotalCount(data.total || list.length);
            setHasMore(Boolean(data.has_more));
            if (data.unread_count !== undefined) setUnreadCount(data.unread_count);
        } catch (err) {
            console.error('Failed to load emails:', err);
            if (skip === 0) setEmails([]);
            const detail = err.response?.data?.detail || '';
            if (typeof detail === 'string' && detail.toLowerCase().includes('not provisioned')) {
                toast.error('Akun Zimbra belum dibuat. Hubungi admin.');
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [selectedFolder, searchQuery]);

    // Gate: Mail only accessible when the user has a workspace that belongs to a company
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Look at the user's workspaces — if ANY is under a company, allow access.
                // Prefer the currently-selected workspace (stored by WorkspaceContext), else any.
                const wsRes = await api.get('/workspaces');
                const workspaces = wsRes.data || [];
                const underCompany = workspaces.some(w => Boolean(w.company_id));
                if (!cancelled) {
                    if (underCompany) {
                        setAllowedAccess(true);
                    } else {
                        toast.error('Menu Mail hanya tersedia untuk workspace di bawah perusahaan');
                        navigate('/');
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    toast.error('Menu Mail hanya tersedia untuk workspace di bawah perusahaan');
                    navigate('/');
                }
            } finally {
                if (!cancelled) setAccessChecking(false);
            }
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    // Initial mount (only after access is granted)
    useEffect(() => {
        if (allowedAccess) loadAccountInfo();
    }, [allowedAccess]);

    // When folder changes
    useEffect(() => {
        if (!allowedAccess) return;
        setSelectedEmail(null);
        setSelectedEmailIds([]);
        setEmails([]);
        (async () => {
            if (!hasSyncedOnce) {
                await handleSync(false);
                setHasSyncedOnce(true);
            } else {
                loadEmails(0);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFolder, allowedAccess]);

    // Debounced search
    useEffect(() => {
        const t = setTimeout(() => loadEmails(0), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Infinite scroll
    const handleScroll = (e) => {
        const el = e.currentTarget;
        if (loadingMore || !hasMore || loading) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
            setLoadingMore(true);
            loadEmails(emails.length, false);
        }
    };

    const handleSync = async (showToast = true) => {
        try {
            setSyncing(true);
            const res = await mailAPI.syncEmails(100);
            if (showToast) {
                const synced = res.data?.synced ?? 0;
                toast.success(`Sinkronisasi selesai: ${synced} email baru`);
            }
            await loadEmails(0);
        } catch (err) {
            console.error('Sync failed:', err);
            if (showToast) {
                toast.error(err.response?.data?.detail || 'Gagal sinkronisasi email');
            }
        } finally {
            setSyncing(false);
        }
    };

    const handleRefresh = () => handleSync(true);

    const handleSelectEmail = (emailId) => {
        setSelectedEmailIds(prev => prev.includes(emailId) ? prev.filter(id => id !== emailId) : [...prev, emailId]);
    };

    const handleSelectAll = () => {
        if (selectedEmailIds.length === filteredEmails.length) setSelectedEmailIds([]);
        else setSelectedEmailIds(filteredEmails.map(e => e.id));
    };

    const handleStarToggle = async (email) => {
        try {
            const newStarred = !email.starred;
            setEmails(prev => prev.map(e => e.id === email.id ? { ...e, starred: newStarred } : e));
            await mailAPI.toggleStar(email.id, newStarred);
        } catch (err) {
            toast.error('Gagal memperbarui bintang');
            setEmails(prev => prev.map(e => e.id === email.id ? { ...e, starred: email.starred } : e));
        }
    };

    const handleImportantToggle = async (email) => {
        try {
            const newImportant = !email.important;
            setEmails(prev => prev.map(e => e.id === email.id ? { ...e, important: newImportant } : e));
            await mailAPI.toggleImportant(email.id, newImportant);
        } catch (err) {
            toast.error('Gagal memperbarui penting');
            setEmails(prev => prev.map(e => e.id === email.id ? { ...e, important: email.important } : e));
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedEmailIds.length === 0) return;
        try {
            await Promise.all(selectedEmailIds.map(id => mailAPI.trashEmail(id)));
            toast.success(`${selectedEmailIds.length} email dipindahkan ke sampah`);
            setSelectedEmailIds([]);
            await loadEmails(0);
        } catch (err) {
            toast.error('Gagal menghapus email');
        }
    };

    const handleOpenEmail = async (email) => {
        // Special case: clicking a draft opens the compose modal
        if (email.folder === 'drafts' || selectedFolder === 'drafts') {
            setComposeDraft(email);
            setShowCompose(true);
            return;
        }
        setSelectedEmail(email);
        if (!email.read) {
            try {
                await mailAPI.markAsRead(email.id, true);
                setEmails(prev => prev.map(e => e.id === email.id ? { ...e, read: true } : e));
                setUnreadCount(c => Math.max(0, c - 1));
            } catch (err) { /* noop */ }
        }
    };

    const handleReply = () => {
        if (!selectedEmail) return;
        const body = `\n\n---\nPada ${formatFullDate(selectedEmail.date)}, ${selectedEmail.from_email} menulis:\n${(selectedEmail.body || '').replace(/<[^>]+>/g, '')}`;
        setComposeDraft({
            to_email: selectedEmail.from_email,
            subject: selectedEmail.subject?.startsWith('Re:') ? selectedEmail.subject : `Re: ${selectedEmail.subject || ''}`,
            body,
        });
        setShowCompose(true);
    };

    const handleForward = () => {
        if (!selectedEmail) return;
        const fwdBody = `\n\n---------- Pesan Diteruskan ----------\nDari: ${selectedEmail.from_email}\nTanggal: ${formatFullDate(selectedEmail.date)}\nSubjek: ${selectedEmail.subject}\n\n${selectedEmail.body || ''}`;
        setComposeDraft({
            subject: selectedEmail.subject?.startsWith('Fwd:') ? selectedEmail.subject : `Fwd: ${selectedEmail.subject || ''}`,
            body: fwdBody,
        });
        setShowCompose(true);
    };

    const handleNewCompose = () => {
        setComposeDraft(null);
        setShowCompose(true);
    };

    // Search is server-side but also filter client-side within loaded page
    const filteredEmails = emails;

    const currentFolder = FOLDERS.find(f => f.id === selectedFolder);

    // While verifying access, show a small spinner screen (prevents flashing Mail UI)
    if (accessChecking || !allowedAccess) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            sidebarCollapsed={effectiveSidebarCollapsed}
            onSidebarMouseEnter={handleSidebarMouseEnter}
            onSidebarMouseLeave={handleSidebarMouseLeave}
            hideHeader
            noPadding
        >
            {/* Invisible edge trigger — only while sidebar is persistently
                hidden and not currently hovered. Hover it to slide the
                sidebar in with the same smooth animation. */}
            {appSidebarHidden && !sidebarHovered && !initialGraceActive && (
                <div
                    className="fixed left-0 top-0 h-screen w-3 z-40"
                    onMouseEnter={handleSidebarMouseEnter}
                    data-testid="sidebar-edge-trigger"
                    aria-hidden="true"
                />
            )}
            <div className="flex h-screen">
                {/* Left Sidebar */}
                <div className="w-64 bg-white border-r border-brand-border flex flex-col">
                    <div className="p-4 flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={toggleAppSidebar} title={appSidebarHidden ? 'Tampilkan sidebar utama' : 'Sembunyikan sidebar utama'} data-testid="toggle-app-sidebar-btn">
                            {appSidebarHidden ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                        </Button>
                        <Button onClick={handleNewCompose} className="flex-1 justify-start gap-2 shadow-md hover:shadow-lg" data-testid="compose-open-btn">
                            <Edit3 className="w-5 h-5" />
                            Tulis
                        </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-2">
                        {FOLDERS.map((folder) => {
                            const Icon = folder.icon;
                            const isActive = selectedFolder === folder.id;
                            return (
                                <button
                                    key={folder.id}
                                    onClick={() => setSelectedFolder(folder.id)}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors",
                                        isActive ? "bg-brand-accent text-brand-primary font-medium" : "hover:bg-brand-accent-soft text-brand-text-secondary"
                                    )}
                                    data-testid={`folder-${folder.id}-btn`}
                                >
                                    <Icon className={cn("w-5 h-5", folder.color)} />
                                    <span className="flex-1 text-left">{folder.label}</span>
                                    {folder.id === 'inbox' && unreadCount > 0 && (
                                        <span className="text-xs bg-brand-primary text-white px-2 py-0.5 rounded-full" data-testid="unread-count-badge">
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {accountInfo && (
                        <div className="p-4 border-t border-brand-border">
                            <p className="text-xs text-brand-text-tertiary truncate" data-testid="mail-account-email">{accountInfo.email}</p>
                            {accountInfo.id && (
                                <ZimbraAccountActions 
                                    accountId={accountInfo.id} 
                                    onResyncComplete={loadEmails}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col bg-brand-bg-primary min-w-0">
                    {/* Top Bar */}
                    <div className="bg-white border-b border-brand-border p-4">
                        <div className="flex items-center gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-tertiary" />
                                <Input
                                    placeholder="Telusuri email"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 bg-brand-accent-soft border-0"
                                    data-testid="mail-search-input"
                                />
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={loading || syncing} data-testid="mail-refresh-btn" title="Sinkronisasi">
                                <RefreshCw className={cn("w-4 h-4", (loading || syncing) && "animate-spin")} />
                            </Button>
                        </div>
                    </div>

                    {/* Action Bar */}
                    {emails.length > 0 && (
                        <div className="bg-white border-b border-brand-border px-4 py-2 flex items-center gap-4">
                            <button onClick={handleSelectAll} className="text-brand-text-secondary hover:text-brand-text-primary" data-testid="mail-select-all-btn">
                                {selectedEmailIds.length === filteredEmails.length && filteredEmails.length > 0 ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                            </button>
                            {selectedEmailIds.length > 0 && (
                                <>
                                    <span className="text-sm text-brand-text-secondary">{selectedEmailIds.length} dipilih</span>
                                    <Button variant="ghost" size="sm" onClick={handleDeleteSelected} data-testid="mail-bulk-delete-btn">
                                        <Trash2 className="w-4 h-4 mr-2" /> Hapus
                                    </Button>
                                </>
                            )}
                            <div className="flex-1" />
                            <span className="text-sm text-brand-text-secondary" data-testid="mail-count-label">
                                {emails.length > 0 ? `1-${emails.length}` : '0'} dari {totalCount}
                            </span>
                        </div>
                    )}

                    {/* Email List */}
                    <div className="flex-1 overflow-y-auto" ref={listRef} onScroll={handleScroll}>
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                            </div>
                        ) : filteredEmails.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-brand-text-secondary">
                                <Mail className="w-16 h-16 mb-4 text-brand-text-tertiary" />
                                <p className="text-lg font-medium">
                                    {searchQuery ? 'Tidak ada email yang cocok' : `Tidak ada email di ${currentFolder?.label}`}
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-brand-border">
                                {filteredEmails.map((email) => {
                                    const isSelected = selectedEmailIds.includes(email.id);
                                    const isActive = selectedEmail?.id === email.id;
                                    return (
                                        <div
                                            key={email.id}
                                            className={cn(
                                                "flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-brand-accent-soft transition-colors",
                                                isActive && "bg-brand-accent",
                                                !email.read && "bg-blue-50/30"
                                            )}
                                            onClick={() => handleOpenEmail(email)}
                                            data-testid={`mail-row-${email.id}`}
                                        >
                                            <button onClick={(e) => { e.stopPropagation(); handleSelectEmail(email.id); }} className="text-brand-text-tertiary hover:text-brand-text-primary" data-testid={`mail-checkbox-${email.id}`}>
                                                {isSelected ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); handleStarToggle(email); }} className="text-brand-text-tertiary hover:text-yellow-500" data-testid={`mail-star-${email.id}`}>
                                                <Star className={cn("w-5 h-5", email.starred && "fill-yellow-500 text-yellow-500")} />
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); handleImportantToggle(email); }} className="text-brand-text-tertiary hover:text-red-500" data-testid={`mail-important-${email.id}`}>
                                                <AlertCircle className={cn("w-4 h-4", email.important && "fill-red-500/20 text-red-500")} />
                                            </button>
                                            <div className="w-48 flex-shrink-0">
                                                <p className={cn("text-sm truncate", !email.read ? "font-semibold text-brand-text-primary" : "text-brand-text-secondary")}>
                                                    {selectedFolder === 'sent' || selectedFolder === 'drafts'
                                                        ? (email.to_email || '(Tanpa penerima)')
                                                        : (email.from_email || 'Unknown')}
                                                </p>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm truncate">
                                                    <span className={cn(!email.read && "font-semibold text-brand-text-primary")}>
                                                        {email.subject || '(Tanpa subjek)'}
                                                    </span>
                                                    <span className="text-brand-text-tertiary ml-2">
                                                        {truncateText(email.preview || email.body)}
                                                    </span>
                                                </p>
                                            </div>
                                            {email.has_attachments && <Paperclip className="w-4 h-4 text-brand-text-tertiary" />}
                                            <div className="w-16 text-right">
                                                <p className="text-xs text-brand-text-tertiary">{formatShortDate(email.date)}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                                {loadingMore && (
                                    <div className="flex items-center justify-center py-4">
                                        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
                                    </div>
                                )}
                                {hasMore && !loadingMore && (
                                    <div className="flex items-center justify-center py-4">
                                        <Button variant="outline" size="sm" onClick={() => { setLoadingMore(true); loadEmails(emails.length, false); }} data-testid="mail-load-more-btn">
                                            Muat lebih banyak
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Email Detail Panel */}
                {selectedEmail && (
                    <div className="w-[640px] bg-white border-l border-brand-border overflow-y-auto" data-testid="mail-detail-panel">
                        <div className="p-6">
                            <div className="flex items-start justify-between mb-4 gap-3">
                                <h2 className="text-xl font-semibold text-brand-text-primary flex-1">
                                    {selectedEmail.subject || '(Tanpa subjek)'}
                                </h2>
                                <Button variant="ghost" size="icon" onClick={() => setSelectedEmail(null)} data-testid="mail-detail-close-btn">
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            {/* Action toolbar */}
                            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-brand-border">
                                <Button variant="outline" size="sm" onClick={handleReply} data-testid="mail-detail-reply-btn">
                                    <Reply className="w-4 h-4 mr-2" /> Balas
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleForward} data-testid="mail-detail-forward-btn">
                                    <Forward className="w-4 h-4 mr-2" /> Teruskan
                                </Button>
                                <Button variant="outline" size="sm" onClick={async () => { await handleStarToggle(selectedEmail); setSelectedEmail(s => s ? { ...s, starred: !s.starred } : s); }}>
                                    <Star className={cn("w-4 h-4 mr-2", selectedEmail.starred && "fill-yellow-500 text-yellow-500")} />
                                    {selectedEmail.starred ? 'Berbintang' : 'Bintangi'}
                                </Button>
                                <div className="flex-1" />
                                <Button variant="ghost" size="sm" onClick={async () => { await mailAPI.trashEmail(selectedEmail.id); toast.success('Dipindahkan ke sampah'); setSelectedEmail(null); loadEmails(0); }} className="text-red-600 hover:text-red-700" data-testid="mail-detail-delete-btn">
                                    <Trash2 className="w-4 h-4 mr-2" /> Hapus
                                </Button>
                            </div>

                            {/* Meta */}
                            <div className="space-y-1.5 mb-6 text-sm">
                                <div className="flex items-start gap-2">
                                    <span className="min-w-[60px] font-medium text-brand-text-secondary">Dari:</span>
                                    <span className="text-brand-text-primary break-all" data-testid="mail-detail-from">{selectedEmail.from_email || '-'}</span>
                                </div>
                                {selectedEmail.to_email && (
                                    <div className="flex items-start gap-2">
                                        <span className="min-w-[60px] font-medium text-brand-text-secondary">Kepada:</span>
                                        <span className="text-brand-text-primary break-all" data-testid="mail-detail-to">{selectedEmail.to_email}</span>
                                    </div>
                                )}
                                {selectedEmail.cc && (
                                    <div className="flex items-start gap-2">
                                        <span className="min-w-[60px] font-medium text-brand-text-secondary">Cc:</span>
                                        <span className="text-brand-text-primary break-all">{selectedEmail.cc}</span>
                                    </div>
                                )}
                                {selectedEmail.bcc && (
                                    <div className="flex items-start gap-2">
                                        <span className="min-w-[60px] font-medium text-brand-text-secondary">Bcc:</span>
                                        <span className="text-brand-text-primary break-all">{selectedEmail.bcc}</span>
                                    </div>
                                )}
                                <div className="flex items-start gap-2 pt-1">
                                    <span className="min-w-[60px] font-medium text-brand-text-secondary">Tanggal:</span>
                                    <span className="text-brand-text-tertiary">{formatFullDate(selectedEmail.date)}</span>
                                </div>
                            </div>

                            {/* Body (sanitized HTML) */}
                            <div
                                className="prose prose-sm max-w-none text-brand-text-primary break-words"
                                data-testid="mail-detail-body"
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.body || selectedEmail.preview || '') }}
                            />

                            {/* Attachments */}
                            {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-brand-border">
                                    <h3 className="font-medium text-brand-text-primary mb-3 flex items-center gap-2">
                                        <Paperclip className="w-4 h-4" />
                                        Lampiran ({selectedEmail.attachments.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedEmail.attachments.map((att, idx) => (
                                            <div key={idx} className="flex items-center gap-3 p-3 bg-brand-bg-secondary rounded-lg">
                                                <Paperclip className="w-4 h-4 text-brand-text-tertiary" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-brand-text-primary truncate">{att.filename}</p>
                                                    {att.size && (
                                                        <p className="text-xs text-brand-text-tertiary">{(att.size / 1024).toFixed(1)} KB</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Compose Modal */}
            <ComposeEmailModalV2
                isOpen={showCompose}
                onClose={() => { setShowCompose(false); setComposeDraft(null); }}
                initialDraft={composeDraft}
                onSent={() => loadEmails(0)}
                onDraftSaved={() => { if (selectedFolder === 'drafts') loadEmails(0); }}
            />
        </Layout>
    );
}
